import { useId, useState } from 'react';
import { Box, FormHelperText, Stack, Typography } from '@mui/material';
import type { FieldElement } from '../../format/form-definition';
import { isReference } from '../../format/pieces';
import { fieldTypeOf } from '../../field-types';
import { ReferenceChip } from '../relationships/ReferenceChip';
import { SwapMenu } from '../relationships/SwapMenu';
import type { Note } from './rangeEntry';
import { fieldInputOf } from '../../rendering/fieldInput';

interface BoundInputProps {
  field: FieldElement;
  name: string;
  kind: 'number' | 'date';
  input: unknown;
  note: Note | undefined;
  fields: FieldElement[];
  onInput: (input: unknown) => void;
  onTypeValue: () => void;
}

export const BoundInput = (props: BoundInputProps) => {
  const { field, name, kind, input, note, fields, onInput, onTypeValue } = props;
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const id = useId();
  const ids = { input: `${id}input`, label: `${id}label`, note: `${id}note` };
  const offersToday = kind === 'date';
  const reference = isReference(input) ? input : undefined;
  const { Input, settings } = fieldInputOf(field);

  const place = reference ? (
    <ReferenceChip
      reference={reference}
      onOpen={setMenuAnchor}
      onClear={() => onInput(fieldTypeOf(field).emptyValue)}
      aria-labelledby={ids.label}
    />
  ) : (
    <Input
      label={name}
      inputId={ids.input}
      labelId={ids.label}
      describedBy={note === undefined ? undefined : ids.note}
      settings={settings}
      value={input}
      onChange={onInput}
      onBlur={() => {}}
      disabled={false}
      required={false}
      error={note?.error ?? false}
      bounds={{}}
      options={[]}
      attachFile={() => {
        throw new Error('The Builder attaches no files.');
      }}
    />
  );

  return (
    <div>
      <Stack useFlexGap direction="row" spacing={1} sx={{ alignItems: 'center', minHeight: 56 }}>
        <Typography
          component="label"
          id={ids.label}
          htmlFor={ids.input}
          variant="body2"
          color="text.secondary"
          sx={{ flex: '0 0 120px' }}
        >
          {name}
        </Typography>
        <Box sx={{ flex: 1, minWidth: 0 }}>{place}</Box>
        <SwapMenu
          title={offersToday ? "Today or another field's value" : "Another field's value"}
          label={`${name}: ${offersToday ? "today or another field's value" : "another field's value"}`}
          anchor={menuAnchor}
          onAnchor={setMenuAnchor}
          plain={kind === 'date' ? 'A date' : 'A number'}
          offersToday={offersToday}
          fields={fields}
          reference={reference}
          onPick={next => (next ? onInput(next) : onTypeValue())}
        />
      </Stack>
      {note && (
        <FormHelperText id={ids.note} error={note.error}>
          {note.text}
        </FormHelperText>
      )}
    </div>
  );
};
